"use client"

import { useEffect, useState } from "react"
import { OVERLAY_EVENTS } from "@/lib/overlay-constants"

const BOTTOM_DOCK_CSS_VAR = "--wg-bottom-dock-height"
// Third-party (ad) portion of the dock, published separately so a bar that
// CONTRIBUTES to the dock can still offset itself. Reading the total would
// include its own height and push it up by its own size on every resize.
const AD_DOCK_CSS_VAR = "--wg-bottom-dock-ad-height"

// First-party docks are route-exclusive and share this key (see below), so
// "not primary-dock" is exactly "not me" for any first-party bar.
const PRIMARY_DOCK_KEY = "primary-dock"

// Realistic dock heights: tool-form narrow CTAs ~120px, plus a Mediavine
// anchor ad (~50-100px) that can coexist with any first-party dock. Anything
// past 280 means a stale/leaked/broken value — clamping keeps the chat FAB
// and TOC FAB on-screen instead of pushing them above the viewport.
const MAX_REASONABLE_DOCK_HEIGHT = 280

function readCssVarHeight(name: string) {
  if (typeof window === "undefined") return 0
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  const height = Number.parseFloat(raw)
  if (!Number.isFinite(height)) return 0
  return Math.min(MAX_REASONABLE_DOCK_HEIGHT, Math.max(0, Math.ceil(height)))
}

function readBottomDockHeight() {
  return readCssVarHeight(BOTTOM_DOCK_CSS_VAR)
}

function readAdDockHeight() {
  return readCssVarHeight(AD_DOCK_CSS_VAR)
}

// Contribution model: first-party docks are route-exclusive (one per page) and
// all write under the "primary-dock" key, preserving the original last-writer
// semantics between them. Third-party bars that can coexist with any dock —
// the Mediavine anchor ad — register under their own key, and the CSS var
// carries the SUM so dock-aware elements clear the full stack.
const contributions = new Map<string, number>()

function writeTotal() {
  let total = 0
  let adTotal = 0
  contributions.forEach((h, key) => {
    total += h
    if (key !== PRIMARY_DOCK_KEY) adTotal += h
  })
  adTotal = Math.min(MAX_REASONABLE_DOCK_HEIGHT, Math.max(0, Math.ceil(adTotal)))
  // Clamp HERE, not just in the readers. The CSS var is consumed directly by
  // `calc()` clamps (the mobile nav drawer and the sticky rails) that never go
  // through readBottomDockHeight/useBottomDockInset, so a clamp applied only on
  // the read side would let a stale or broken contribution reach CSS while JS
  // consumers correctly rejected it — the two would silently disagree.
  total = Math.min(MAX_REASONABLE_DOCK_HEIGHT, Math.max(0, Math.ceil(total)))

  if (total > 0) {
    document.documentElement.style.setProperty(BOTTOM_DOCK_CSS_VAR, `${total}px`)
  } else {
    document.documentElement.style.removeProperty(BOTTOM_DOCK_CSS_VAR)
  }

  if (adTotal > 0) {
    document.documentElement.style.setProperty(AD_DOCK_CSS_VAR, `${adTotal}px`)
  } else {
    document.documentElement.style.removeProperty(AD_DOCK_CSS_VAR)
  }

  window.dispatchEvent(
    new CustomEvent(OVERLAY_EVENTS.BOTTOM_DOCK_CHANGED, {
      detail: { height: total, adHeight: adTotal },
    }),
  )
}

export function setBottomDockContribution(source: string, height: number) {
  if (typeof window === "undefined") return

  const next = Math.max(0, Math.ceil(height))
  if (next > 0) {
    contributions.set(source, next)
  } else {
    contributions.delete(source)
  }
  writeTotal()
}

export function setBottomDockHeight(height: number) {
  setBottomDockContribution(PRIMARY_DOCK_KEY, height)
}

/**
 * Inset a first-party sticky bar must apply to clear third-party bars beneath
 * it — today the Mediavine anchor ad and Universal Player.
 *
 * A bar that calls `setBottomDockHeight` must use THIS, not `useBottomDockInset`:
 * the latter includes the caller's own contribution, so the bar would offset
 * itself by its own height and climb on every resize.
 *
 * This is not hypothetical tidiness. Seven sticky bars registered their height
 * and never read anything back, so they sat at `bottom: 0` underneath the ad.
 * On mobile `/tools/seeding-calculator` the Universal Player covered the left
 * half of the "BEST WINDOW" result bar and won the hit test.
 */
export function useAdDockInset() {
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const syncHeight = (event?: Event) => {
      if (event instanceof CustomEvent && typeof event.detail?.adHeight === "number") {
        setHeight(Math.min(MAX_REASONABLE_DOCK_HEIGHT, Math.max(0, Math.ceil(event.detail.adHeight))))
        return
      }

      setHeight(readAdDockHeight())
    }

    syncHeight()
    window.addEventListener(OVERLAY_EVENTS.BOTTOM_DOCK_CHANGED, syncHeight)
    return () => window.removeEventListener(OVERLAY_EVENTS.BOTTOM_DOCK_CHANGED, syncHeight)
  }, [])

  return height
}

export function useBottomDockInset() {
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const syncHeight = (event?: Event) => {
      if (event instanceof CustomEvent && typeof event.detail?.height === "number") {
        setHeight(Math.min(MAX_REASONABLE_DOCK_HEIGHT, Math.max(0, Math.ceil(event.detail.height))))
        return
      }

      setHeight(readBottomDockHeight())
    }

    syncHeight()
    window.addEventListener(OVERLAY_EVENTS.BOTTOM_DOCK_CHANGED, syncHeight)
    return () => window.removeEventListener(OVERLAY_EVENTS.BOTTOM_DOCK_CHANGED, syncHeight)
  }, [])

  return height
}
